import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../utils/axiosInstance";
import { useAuth } from "./AuthContext";

const WorkspaceContext = createContext();

function persistWorkspace(workspace) {
  if (workspace?.id) {
    localStorage.setItem("activeWorkspaceId", workspace.id);
  } else {
    localStorage.removeItem("activeWorkspaceId");
  }
}

export function WorkspaceProvider({ children }) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [renaming, setRenaming] = useState(false);

  const refreshWorkspaces = useCallback(async () => {
    if (!isAuthenticated) {
      setWorkspaces([]);
      setActiveWorkspace(null);
      persistWorkspace(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await api.get("/api/workspaces");
      const nextWorkspaces = res.data?.workspaces || [];
      const nextActive = res.data?.activeWorkspace || nextWorkspaces[0] || null;
      setWorkspaces(nextWorkspaces);
      setActiveWorkspace(nextActive);
      persistWorkspace(nextActive);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authLoading) refreshWorkspaces().catch(() => setLoading(false));
  }, [authLoading, refreshWorkspaces]);

  const switchWorkspace = useCallback(async (workspaceId) => {
    if (!workspaceId || workspaceId === activeWorkspace?.id) return activeWorkspace;
    setSwitching(true);
    try {
      const res = await api.post(`/api/workspaces/${workspaceId}/switch`);
      const nextActive = res.data?.activeWorkspace;
      if (nextActive) {
        setActiveWorkspace(nextActive);
        setWorkspaces((prev) => prev.map((w) => (w.id === nextActive.id ? nextActive : w)));
        persistWorkspace(nextActive);
        window.dispatchEvent(new CustomEvent("workspace-changed", { detail: nextActive }));
        window.location.reload();
      }
      return nextActive;
    } finally {
      setSwitching(false);
    }
  }, [activeWorkspace]);

  const renameWorkspace = useCallback(async (workspaceId, name) => {
    const nextName = String(name || "").trim();
    if (!workspaceId || !nextName) return null;

    setRenaming(true);
    try {
      const res = await api.patch(`/api/workspaces/${workspaceId}`, { name: nextName });
      const nextWorkspace = res.data?.workspace;
      if (nextWorkspace) {
        setWorkspaces((prev) => prev.map((w) => (w.id === nextWorkspace.id ? { ...w, ...nextWorkspace } : w)));
        if (activeWorkspace?.id === nextWorkspace.id) {
          const nextActive = { ...activeWorkspace, ...nextWorkspace };
          setActiveWorkspace(nextActive);
          persistWorkspace(nextActive);
          window.dispatchEvent(new CustomEvent("workspace-changed", { detail: nextActive }));
        }
      }
      return nextWorkspace;
    } finally {
      setRenaming(false);
    }
  }, [activeWorkspace]);

  const value = useMemo(() => ({
    workspaces,
    activeWorkspace,
    loading,
    switching,
    renaming,
    refreshWorkspaces,
    switchWorkspace,
    renameWorkspace,
  }), [workspaces, activeWorkspace, loading, switching, renaming, refreshWorkspaces, switchWorkspace, renameWorkspace]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  return useContext(WorkspaceContext);
}
